import {
  BookmarkBorder as BookmarkBorderIcon,
  FiberManualRecord as FiberManualRecordIcon,
  FavoriteBorderRounded as FavoriteBorderRoundedIcon,
  MoreVert as MoreVertIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import {
  Avatar,
  Grid,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextIconLabel from 'src/components/TextIconLabel';
import { PostUtil } from 'src/pages/new-post-new/post-util';
import { CommonHelper } from 'src/utils/commonHelper';
import ThreadsRetweetIcon from '../../../assets/twitter_retweet.svg';
import ThreadsCommentIcon from '../../../assets/comment.svg';
import ThreadsShareIcon from '../../../assets/instagram-share-icon.svg';

const ThreadsPostCard = ({ 
  media_urls, 
  description, 
  profile, 
  share_count = 0, 
  comment_count = 0, 
  like_count = 0,
  handleFocusComment 
}) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const respwidth = useMediaQuery(theme.breakpoints.up('xl'));
  const [showFullDescription, setShowFullDescription] = useState(false);



  let mediaFiles = media_urls || [];
  const connectedProfiles =  profile;

  const hasManageLikesCommentPermission = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.MANAGE_COMMENTS_LIKES
  );

  const handleToggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  // Media layout functions from ThreadsPostPreview
  const getBorderRadius = (i, totalMedia) => {
    const radius = '16px';
    if (totalMedia === 1) return radius;
    if (totalMedia === 2) return i === 0 ? `${radius} 0 0 ${radius}` : `0 ${radius} ${radius} 0`;
    if (totalMedia === 3) {
      if (i === 0) return `${radius} 0 0 0`;
      if (i === 1) return `0 ${radius} 0 0`;
      if (i === 2) return `0 0 ${radius} ${radius}`;
    }
    if (totalMedia >= 4) {
      if (i === 0) return `${radius} 0 0 0`;
      if (i === 1) return `0 ${radius} 0 0`;
      if (i === 2) return `0 0 0 ${radius}`;
      if (i === 3) return `0 0 ${radius} 0`;
    }
    return '0';
  };

  const mediaView = () => {
    let view = [];
    let xWidth = 6;
    let isThreeMedia = false;
    const totalMedia = mediaFiles.length;
    const remainingMediaCount = totalMedia > 4 ? totalMedia - 4 : 0;

    switch (totalMedia) {
      case 1:
        xWidth = 12;
        break;
      case 2:
      case 4:
      default:
        xWidth = 6;
        break;
      case 3:
        isThreeMedia = true;
        break;
    }

    if (isThreeMedia) {
      const gridItemStyle = { position: 'relative', overflow: 'hidden' };
      view = (
        <Grid container spacing={0.2} sx={{ position: 'relative' }}>
          <Grid item xs={6} sx={gridItemStyle}>
            {renderMedia(mediaFiles[0], 0, 300)}
          </Grid>
          <Grid item xs={6}>
            <Grid container direction="column" spacing={0.2}>
              {[1, 2].map((i) => (
                <Grid item xs={12} key={i} sx={gridItemStyle}>
                  {renderMedia(mediaFiles[i], i, 150)}
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      );
    } else {
      view = mediaFiles?.slice(0, 4).map((media, i) => {
        const gridItemStyle = {
          maxWidth: 500,
          maxHeight: 250,
          overflow: 'hidden',
          position: 'relative',
          borderRadius: getBorderRadius(i, totalMedia),
        };
        return (
          <Grid
            item
            xs={xWidth}
            key={i}
            sx={gridItemStyle}
          >
            {renderMedia(media, i, 250, remainingMediaCount)}
          </Grid>
        );
      });
    }

    return (
      <Grid container spacing={0.2} sx={{ position: 'relative' }}>
        {view}
      </Grid>
    );
  };

  const renderMedia = (media, index, height, remainingCount = 0) => {
    const src = media?.media_urls || media;
    const isVideo = media?.media_type.toLowerCase() === PostUtil.MediaType.VIDEO;
    const isImage = media?.media_type.toLowerCase() === PostUtil.MediaType.IMAGE

    const mediaStyle = {
      width: '100%',
      height,
      objectFit: 'cover',
      borderRadius: 'inherit',
    };

    let borderRadiusForItem = 'inherit';
    if (mediaFiles.length === 3 && index === 0) borderRadiusForItem = '12px 0 0 12px';
    if (mediaFiles.length === 3 && index === 1) borderRadiusForItem = '0 12px 0 0';
    if (mediaFiles.length === 3 && index === 2) borderRadiusForItem = '0 0 12px 0';

    return (
      <>
        {isImage ? (
          <img
            src={src}
            alt={`media-${index}`}
            style={{ ...mediaStyle, borderRadius: borderRadiusForItem }}
          />
        ) : isVideo ? (
          <video
            src={src}
            controls
            style={{ ...mediaStyle, borderRadius: borderRadiusForItem }}
          />
        ) : null}

        {index === 3 && remainingCount > 0 && (
          <Typography
            variant="h2"
            color="white"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              borderRadius: 'inherit',
            }}
          >
            +{remainingCount}
          </Typography>
        )}
      </>
    );
  };

  const ProfileTime = () => {
    return (
      <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" sx={{ gap: '0.3rem' }}>
        <Typography variant="span">
          {profile?.date ? CommonHelper.DateTimeFromCurrentTime(profile.date) : translate('preview.just_now')}
        </Typography>
        <FiberManualRecordIcon sx={{ fontSize: 5, mt: '0.6rem' }} />
        <PublicIcon sx={{ fontSize: 15, mt: '0.3rem' }} />
      </Stack>
    );
  };

  return (
    <Stack container justifyContent="center" alignItems="center">
      <Card
        sx={{
          maxWidth: 500,
          minWidth: respwidth ? 400 : null,
          boxShadow: 3,
          borderRadius: 2,
          border: 'none',
          borderColor: theme.palette.preview.postcard.color,
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              src={
                connectedProfiles.connected_profile_image || 
                connectedProfiles.profile_image ||
                connectedProfiles.connected_profile_name || 
                connectedProfiles.connected_profile_id ||
                connectedProfiles.profile_name
              }
              alt={
                connectedProfiles.connected_profile_name ||
                connectedProfiles.profile_name ||
                connectedProfiles.connected_profile_id
              }
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                height: 40,
                width: 40,
                bgcolor: theme.palette.primary.main,
              }}
              style={{ color: 'white' }}
            />
          }
          title={connectedProfiles?.connected_profile_name || connectedProfiles?.profile_name}
          sx={{ fontSize: { xl: 15, lg: 13, md: 11, sm: 9, xs: 7 } }}
          subheader={<ProfileTime />}
          action={<MoreVertIcon sx={{ mt: 1 }} />}
        />
        
        <CardContent sx={{ pt: 1, pb: 1 }}>
          <Typography
            noWrap={!showFullDescription}
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: showFullDescription ? 'initial' : 2,
              overflow: 'hidden',
              WebkitBoxDecorationBreak: 'clone',
              whiteSpace: 'pre-line',
              textOverflow: 'ellipsis',
              fontSize: '0.875rem',
            }}
          >
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>
              {connectedProfiles?.connected_profile_name || connectedProfiles?.profile_name}
            </span>
            {CommonHelper.ParseDescription(description)}
          </Typography>
          {!showFullDescription && (description)?.length > 100 && (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: theme.palette.primary.main,
              }}
              onClick={handleToggleDescription}
            >
              {translate('preview.show_more')}
            </Typography>
          )}
          {showFullDescription && (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: theme.palette.primary.main,
              }}
              onClick={handleToggleDescription}
            >
              {translate('preview.show_less')}
            </Typography>
          )}
        </CardContent>

        <CardMedia sx={{ pl: 2, pr: 2 }}>
          {mediaFiles.length > 0 ? mediaView() : null}
        </CardMedia>

        <CardActions disableSpacing sx={{ display: 'block' }}>
          <Divider />
          <Stack direction="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Paper sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <TextIconLabel
                aria-label="likes"
                sx={{ typography: 'subtitle2', marginRight: '0.5rem', color: theme.palette.preview.postcard.icon }}
                value={like_count !== 0 ? like_count : ''}
                icon={<FavoriteBorderRoundedIcon />}
              />
</Paper>
            <Paper sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <TextIconLabel
                aria-label="comments"
                sx={{ typography: 'subtitle2', marginLeft: '1rem' }}
                value={comment_count !== 0 ? comment_count : ''}
                icon={
                  <IconButton 
                    sx={{ p: 0 }} 
                    onClick={handleFocusComment} 
                    disabled={!hasManageLikesCommentPermission}
                  >
                    <img
                      src={ThreadsCommentIcon}
                      alt="comment"
                      style={{ height: 'auto', width: '100%', maxWidth: '22px', maxHeight: '22px' }}
                    />
                  </IconButton>
                }
              />
</Paper>
            <Paper sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>

              <TextIconLabel
                aria-label="retweets"
                sx={{ typography: 'subtitle2', marginLeft: '1rem' }}
                value={''} // Add retweet count if available
                icon={
                  <img
                    src={ThreadsRetweetIcon}
                    alt="retweet"
                    style={{ height: 'auto', width: '100%', maxWidth: '22px', maxHeight: '22px' }}
                  />
                }
              />
</Paper>
            <Paper sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>

              <TextIconLabel
                aria-label="shares"
                sx={{ typography: 'subtitle2', marginLeft: '1rem' }}
                value={share_count !== 0 ? share_count : ''}
                icon={
                  <img
                    src={ThreadsShareIcon}
                    alt="share"
                    style={{ height: 'auto', width: '100%', maxWidth: '22px', maxHeight: '22px' }}
                  />
                }
              />
            </Paper>
          </Stack>
        </CardActions>
      </Card>
    </Stack>
  );
};

export default ThreadsPostCard;